<script lang="ts" setup generic="T">
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import { type LabelValuePair } from '../types';

const props = withDefaults(
  defineProps<{
    name: string;
    value?: T;
    options?: LabelValuePair<T>[];
    readonly?: boolean;
    disabled?: boolean;
    hasError?: boolean;
    fullWidth?: boolean;
  }>(),
  {
    options: () => [],
    readonly: false,
    disabled: false,
    hasError: false,
  },
);

const emit = defineEmits<{
  'update:value': [T];
}>();

const val = useVModel(props, 'value', emit);

const labelForValue = computed(() => props.options.find((x) => x.value === val.value)?.label);
</script>

<template>
  <div v-if="!readonly" class="inline-flex flex-wrap gap-2" :class="[{ error: hasError }]">
    <div
      v-for="(option, index) in options"
      :key="index"
      :class="{ 'flex flex-grow basis-auto': props.fullWidth }"
    >
      <label
        class="mb-0 flex select-none items-center rounded-lg border px-4 py-2.5"
        :class="{
          '!border-red ': props.hasError,
          '!border-grey-250': !props.hasError,
          '!cursor-pointer': !props.disabled,
          '!cursor-not-allowed bg-grey-50 text-grey-400': props.disabled,
          '!w-full': props.fullWidth,
        }"
      >
        <input
          type="radio"
          class="grid aspect-square h-auto w-[1.15em] flex-shrink-0 basis-auto appearance-none place-content-center rounded-full border border-solid before:aspect-square before:w-[.65em] before:scale-0 before:bg-primary before:shadow-[inset_1em_1em_#fff] before:clip-path-checkmark checked:before:scale-100 disabled:before:shadow-[inset_1em_1em_var(--grey-dark)] checked:disabled:cursor-not-allowed checked:disabled:border-grey-500 checked:disabled:bg-grey-300"
          :class="{
            '!border-grey-500 checked:!border-primary checked:!bg-primary checked:focus:!border-primary-600 checked:focus:!bg-primary-600':
              !props.hasError && !props.disabled,
            'border-red checked:bg-red': props.hasError,
            'cursor-pointer bg-white': !props.disabled,
            'cursor-not-allowed border-grey-400 bg-grey-200': props.disabled,
          }"
          :name="name"
          :id="name + option.value"
          :aria-label="name + option.value"
          v-model="val"
          :value="option.value"
          :disabled="props.disabled"
          :checked="val === option.value"
          data-testid="radio-field-input"
        />
        <span class="ml-2" data-testid="radio-field-label">{{ option.label }}</span>
      </label>
    </div>
  </div>
  <div v-else>{{ labelForValue || '-' }}</div>
</template>
